import React, { createContext, useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { tourConfigs } from './configs/tourConfigs'

import { waitForElement } from './utils/tourHelpers'

import { TourContext } from './TourContext'

export const TourProvider = ({ children }) => {
    const { t } = useTranslation()
    const [isTourActive, setIsTourActive] = useState(false)
    const [currentTourId, setCurrentTourId] = useState(null)
    const [currentStepIndex, setCurrentStepIndex] = useState(0)
    const [tourSteps, setTourSteps] = useState([])
    const [pausedTours, setPausedTours] = useState([])
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isNextDisabled, setIsNextDisabled] = useState(false)
    const [tourOptions, setTourOptions] = useState({})

    const startTour = useCallback((tourId, params) => {
        console.log('=== TourProvider.startTour called ===')
        console.log('tourId:', tourId)
        console.log('params:', params)
        console.log('params type:', typeof params)
        console.log('Available tour configs:', tourConfigs)

        let config = tourConfigs[tourId]
        console.log('Config found:', config)
        console.log('Config is function?', typeof config === 'function')

        // If config is a function, call it with t and params
        if (typeof config === 'function') {
            console.log('Calling config function with params:', params)
            config = config(t, params)
            console.log('Config after calling function:', config)
        }

        if (config && config.steps && config.steps.length > 0) {
            console.log('Setting tour with', config.steps.length, 'steps')
            setCurrentTourId(tourId)
            setTourSteps(config.steps)
            setCurrentStepIndex(0)
            setIsTourActive(true)
            setIsModalOpen(false)
            // Initialize next button state for first step
            setIsNextDisabled(!!config.steps[0].disableNextInitially)
        } else {
            console.error(`Tour configuration not found for ID: ${tourId}`)
            console.log('Config found:', config)
        }
    }, [])

    const resumeTour = useCallback(() => {
        if (pausedTours.length > 0) {
            const lastPausedTour = pausedTours[pausedTours.length - 1]
            // Use saved steps if available, otherwise fallback to config (for backward compatibility or static tours)
            let steps = lastPausedTour.steps

            if (!steps) {
                let config = tourConfigs[lastPausedTour.id]
                if (typeof config === 'function') {
                    config = config(t)
                }
                if (config) {
                    steps = config.steps
                }
            }

            if (steps && steps.length > 0) {
                // Remove the resumed tour from the stack
                setPausedTours(prev => prev.slice(0, -1))

                setCurrentTourId(lastPausedTour.id)
                setTourSteps(steps)
                setCurrentStepIndex(lastPausedTour.stepIndex)
                setIsTourActive(true)
                setIsModalOpen(false)
                setIsNextDisabled(!!steps[lastPausedTour.stepIndex].disableNextInitially)
            }
        }
    }, [pausedTours])

    const endTour = useCallback((resume = true) => {
        let currentConfig = currentTourId ? tourConfigs[currentTourId] : null
        if (typeof currentConfig === 'function') {
            currentConfig = currentConfig(t)
        }
        const shouldAutoResume = resume && !currentConfig?.disableAutoResume

        setIsTourActive(false)
        setCurrentTourId(null)
        setTourSteps([])
        setCurrentStepIndex(0)
        setIsNextDisabled(false)

        if (shouldAutoResume && pausedTours.length > 0) {
            resumeTour()
        }
    }, [pausedTours, resumeTour, currentTourId])

    const pauseTour = useCallback(() => {
        if (currentTourId) {
            setPausedTours(prev => [...prev, {
                id: currentTourId,
                stepIndex: currentStepIndex,
                steps: tourSteps
            }])
            // End current tour without resuming (pass false)
            // But wait, endTour is defined above.
            // We need to make sure endTour doesn't trigger resume if we are pausing.
            // So we pass false to endTour.

            // However, endTour definition above uses `resume = true`.
            // So we must call it with false.

            // But `endTour` is a dependency of `pauseTour`.
            // And `endTour` depends on `resumeTour`.
            // And `resumeTour` depends on `pausedTours`.
            // And `pauseTour` depends on `pausedTours` (via setPausedTours? no).

            // This circular dependency might be annoying.
            // Let's define the logic inside.

            setIsTourActive(false)
            setCurrentTourId(null)
            setTourSteps([])
            setCurrentStepIndex(0)
            setIsNextDisabled(false)
        }
    }, [currentTourId, currentStepIndex])

    const nextStep = useCallback(async () => {
        if (currentStepIndex < tourSteps.length - 1) {
            const nextIndex = currentStepIndex + 1
            const nextStepConfig = tourSteps[nextIndex]

            // Reset disabled state for the new step
            setIsNextDisabled(!!nextStepConfig.disableNextInitially)

            if (nextStepConfig.waitFor) {
                try {
                    await waitForElement(nextStepConfig.waitFor)
                    setCurrentStepIndex(nextIndex)
                } catch (error) {
                    console.error('Tour wait failed:', error)
                    // Optionally handle error, e.g., end tour or skip step
                }
            } else {
                setCurrentStepIndex(nextIndex)
            }
        } else {
            endTour()
        }
    }, [currentStepIndex, tourSteps, endTour])

    const prevStep = useCallback(() => {
        if (currentStepIndex > 0) {
            const prevIndex = currentStepIndex - 1
            setCurrentStepIndex(prevIndex)
            // Reset disabled state for the previous step (optional, maybe keep it enabled if already visited?)
            // For now, reset based on config to be safe
            setIsNextDisabled(!!tourSteps[prevIndex].disableNextInitially)
        }
    }, [currentStepIndex, tourSteps])

    const goToStep = useCallback((index) => {
        if (index >= 0 && index < tourSteps.length) {
            setCurrentStepIndex(index)
            setIsNextDisabled(!!tourSteps[index].disableNextInitially)
        }
    }, [tourSteps])

    const openTourModal = useCallback((options = {}) => {
        setTourOptions(options)
        setIsModalOpen(true)
    }, [])

    const closeTourModal = useCallback(() => {
        setIsModalOpen(false)
    }, [])

    const enableNext = useCallback(() => setIsNextDisabled(false), [])
    const disableNext = useCallback(() => setIsNextDisabled(true), [])

    const updateTourSteps = useCallback((newSteps) => {
        if (isTourActive && newSteps && newSteps.length > 0) {
            console.log('Updating tour steps dynamically:', newSteps.length)
            setTourSteps(newSteps)
            // Ensure current step index is valid for new steps
            if (currentStepIndex >= newSteps.length) {
                setCurrentStepIndex(newSteps.length - 1)
            }
        }
    }, [isTourActive, currentStepIndex])

    const value = {
        isTourActive,
        currentTourId,
        currentStepIndex,
        currentStep: tourSteps[currentStepIndex],
        totalSteps: tourSteps.length,
        isNextDisabled,
        pausedTours,
        startTour,
        endTour,
        pauseTour,
        resumeTour,
        nextStep,
        prevStep,
        goToStep,
        isModalOpen,
        openTourModal,
        closeTourModal,
        tourOptions,
        enableNext,
        disableNext,
        updateTourSteps
    }

    return (
        <TourContext.Provider value={value}>
            {children}
        </TourContext.Provider>
    )
}
